import { useState, useEffect, useCallback, useMemo } from 'react';
import { Package, Plus, Edit3, BarChart2, Trash2, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLabels } from '../hooks/useLabels';
import { useTenantFormat } from '../hooks/useTenantFormat';
import { useToast } from '../hooks/useToast';
import { api } from '../lib/api';
import GlassTable from '../components/GlassTable';
import Drawer from '../components/Drawer';
import ConfirmModal from '../components/ConfirmModal';
import InventoryIntelPanel from '../components/InventoryIntelPanel';
import BakemillInventory from '../components/connected/BakemillInventory';

const INPUT = 'w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-900 text-sm placeholder:text-gray-400 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition-colors';

export default function Inventory() {
  const { token } = useAuth();
  const labels = useLabels();
  const { formatMoney, currencySymbol, taxLabel } = useTenantFormat();
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [adjustItem, setAdjustItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    try { const r = await api.productsList(token); setItems(r.items || []); }
    catch (e) { toast.show(e?.message || 'Failed to load inventory', 'error'); }
    finally { setLoading(false); }
  }, [token, toast]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(p => `${p.name} ${p.sku} ${p.hsn_code || ''}`.toLowerCase().includes(q));
  }, [items, search]);

  const handleDelete = async () => {
    if (!deleteItem) return;
    setDeleting(true);
    try { await api.productDelete(token, deleteItem.id); toast.show(`${deleteItem.name} deleted`, 'success'); setDeleteItem(null); load(); }
    catch (e) { toast.show(e?.message || 'Delete failed', 'error'); }
    finally { setDeleting(false); }
  };

  const columns = [
    { key: 'name', label: 'Name', render: (_, r) => (
      <div><p className="text-white font-semibold">{r.name}</p>{r.hsn_code && <p className="text-white/40 text-xs mt-0.5">HSN {r.hsn_code}</p>}</div>
    )},
    { key: 'sku', label: 'SKU' },
    { key: 'sell_price', label: 'Sell Price', align: 'right', render: v => <span className="text-white/90 font-medium">{formatMoney(v)}</span> },
    { key: 'cost_price', label: 'Cost Price', align: 'right', render: v => <span className="text-white/60">{formatMoney(v)}</span> },
    { key: 'stock_qty', label: 'Stock', align: 'right', render: (v, r) => {
      const low = r.low_stock_threshold != null && v <= r.low_stock_threshold;
      return (<div className="flex items-center justify-end gap-2">
        <span className={`font-bold ${low ? 'text-red-400' : 'text-white'}`}>{v ?? '—'}</span>
        {r.unit && <span className="text-white/40 text-xs">{r.unit}</span>}
        {low && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/15 border border-red-500/25 text-red-400 text-[10px] font-bold" data-testid="low-stock-badge"><AlertTriangle size={10} />Low</span>}
      </div>);
    }},
    { key: 'is_active', label: 'Status', align: 'center', render: v => (
      <span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-bold ${v !== false ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/25' : 'bg-white/5 text-white/40 border border-white/10'}`}>{v !== false ? 'Active' : 'Inactive'}</span>
    )},
  ];

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="inventory-page">
      {/* Bakemill ERP stock (renders nothing for other tenants) */}
      <BakemillInventory />

      {/* AI Inventory Intelligence Panel */}
      <InventoryIntelPanel />

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-extrabold text-white tracking-tight" data-testid="inventory-title">{labels.inventory}</h1>
          <p className="text-white/40 text-sm mt-1">{items.length} {labels.products.toLowerCase()}</p>
        </div>
        <button data-testid="add-product-btn" onClick={() => setAddOpen(true)}
          className="flex items-center gap-2 px-5 h-11 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 text-white text-sm font-bold hover:shadow-lg hover:shadow-teal-500/25 transition-all">
          <Plus size={18} /> Add {labels.product}
        </button>
      </div>
      <GlassTable testId="inventory-table" columns={columns} data={filtered} loading={loading}
        emptyMessage={search ? 'No matches found' : `No ${labels.products.toLowerCase()} yet`}
        emptyIcon={<Package size={40} className="text-white/20" />}
        searchValue={search} onSearchChange={setSearch} searchPlaceholder="Search name, SKU or HSN..."
        actions={(row) => (<> {/* eslint-disable-line react/no-unstable-nested-components */}
          <button data-testid={`edit-${row.id}`} onClick={e => { e.stopPropagation(); setEditItem(row); }} className="p-2 rounded-lg hover:bg-white/10 text-white/50 hover:text-white transition-colors" title="Edit"><Edit3 size={16} /></button>
          <button data-testid={`adjust-${row.id}`} onClick={e => { e.stopPropagation(); setAdjustItem(row); }} className="p-2 rounded-lg hover:bg-white/10 text-white/50 hover:text-teal-400 transition-colors" title="Adjust Stock"><BarChart2 size={16} /></button>
          <button data-testid={`delete-${row.id}`} onClick={e => { e.stopPropagation(); setDeleteItem(row); }} className="p-2 rounded-lg hover:bg-white/10 text-white/50 hover:text-red-400 transition-colors" title="Delete"><Trash2 size={16} /></button>
        </>)} />
      <ProductDrawer open={addOpen || !!editItem} item={editItem} onClose={() => { setAddOpen(false); setEditItem(null); }} token={token} onSuccess={() => { setAddOpen(false); setEditItem(null); load(); }} label={labels.product} />
      <AdjustStockDrawer open={!!adjustItem} item={adjustItem} onClose={() => setAdjustItem(null)} token={token} onSuccess={() => { setAdjustItem(null); load(); }} />
      <ConfirmModal open={!!deleteItem} onClose={() => setDeleteItem(null)} onConfirm={handleDelete}
        title={`Delete ${deleteItem?.name}?`} message="This action cannot be undone." confirmText="Delete" variant="danger" loading={deleting} />
    </div>
  );
}

function ProductDrawer({ open, item, onClose, token, onSuccess, label }) {
  const toast = useToast();
  const { currencySymbol: pcs, taxLabel: ptl } = useTenantFormat();
  const isEdit = !!item;
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) setForm({ name: item.name||'', sku: item.sku||'', hsn_code: item.hsn_code||'', unit: item.unit||'', stock_qty: String(item.stock_qty??''), low_stock_threshold: String(item.low_stock_threshold??''), cost_price: String(item.cost_price??''), sell_price: String(item.sell_price??''), gst_rate: String(item.gst_rate??'18'), is_active: item.is_active!==false });
    else setForm({ name:'', sku:'', hsn_code:'', unit:'pcs', stock_qty:'0', low_stock_threshold:'5', cost_price:'', sell_price:'', gst_rate:'18', is_active:true });
  }, [item, open]);

  const submit = async () => {
    if (!form.name?.trim()) { toast.show('Name required', 'error'); return; }
    if (!form.sku?.trim()) { toast.show('SKU required', 'error'); return; }
    setSaving(true);
    try {
      const body = { name: form.name.trim(), sku: form.sku.trim(), hsn_code: form.hsn_code||undefined, unit: form.unit||undefined, stock_qty: parseFloat(form.stock_qty)||0, low_stock_threshold: form.low_stock_threshold ? parseFloat(form.low_stock_threshold) : undefined, cost_price: form.cost_price ? parseFloat(form.cost_price) : undefined, sell_price: form.sell_price ? parseFloat(form.sell_price) : undefined, gst_rate: form.gst_rate ? parseFloat(form.gst_rate) : undefined, is_active: form.is_active };
      if (isEdit) { await api.productPatch(token, item.id, body); toast.show(`${label} updated`, 'success'); }
      else { await api.productsCreate(token, body); toast.show(`${label} created`, 'success'); }
      onSuccess();
    } catch (e) { toast.show(e?.message || 'Save failed', 'error'); } finally { setSaving(false); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <Drawer open={open} onClose={onClose} title={isEdit ? `Edit ${label}` : `Add ${label}`}>
      <div className="space-y-4">
        {[['name','Name *','text'],['sku','SKU *','text'],['hsn_code','HSN Code','text'],['unit','Unit (pcs, kg)','text'],['sell_price',`Sell Price (${pcs})`,'number'],['cost_price',`Cost Price (${pcs})`,'number'],['stock_qty','Stock Quantity','number'],['low_stock_threshold','Low Stock Alert At','number'],['gst_rate',`${ptl} Rate (%)`,'number']].map(([k,l,t]) => (
          <label key={k} className="block" htmlFor={`prod-${k}`}>
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{l}</span>
            <input id={`prod-${k}`} data-testid={`product-field-${k}`} type={t} className={`mt-1.5 ${INPUT}`} value={form[k]??''} onChange={e => set(k, e.target.value)} placeholder={l.replace(' *','')} />
          </label>
        ))}
        <label className="flex items-center gap-3 cursor-pointer" htmlFor="prod-active">
          <input id="prod-active" type="checkbox" checked={form.is_active??true} onChange={e => set('is_active', e.target.checked)} className="w-5 h-5 rounded border-gray-300 bg-white accent-teal-500" />
          <span className="text-sm text-gray-700 font-medium">Active</span>
        </label>
        <button data-testid="product-save-btn" onClick={submit} disabled={saving}
          className="w-full h-12 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-bold text-sm hover:shadow-lg hover:shadow-teal-500/25 transition-all disabled:opacity-50 mt-4">
          {saving ? <div className="w-5 h-5 border-2 border-white/50 border-t-white rounded-full animate-spin mx-auto" /> : (isEdit ? 'Save Changes' : `Add ${label}`)}
        </button>
      </div>
    </Drawer>
  );
}

function AdjustStockDrawer({ open, item, onClose, token, onSuccess }) {
  const toast = useToast();
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setQty(''); setReason(''); }, [open]);

  const submit = async () => {
    if (!qty || parseFloat(qty) === 0) { toast.show('Quantity required', 'error'); return; }
    setSaving(true);
    try { await api.inventoryAdjust(token, { product_id: item.id, adjustment_qty: parseFloat(qty), reason: reason||undefined }); toast.show(`Stock adjusted for ${item.name}`, 'success'); onSuccess(); }
    catch (e) { toast.show(e?.message || 'Failed', 'error'); } finally { setSaving(false); }
  };

  return (
    <Drawer open={open} onClose={onClose} title={`Adjust Stock — ${item?.name || ''}`}>
      <div className="space-y-4">
        <div className="bg-gray-50 rounded-xl border border-gray-200 p-4"><p className="text-gray-500 text-xs uppercase font-semibold tracking-wider">Current Stock</p><p className="text-2xl font-extrabold text-gray-900 mt-1">{item?.stock_qty ?? 0} <span className="text-gray-400 text-sm font-normal">{item?.unit||''}</span></p></div>
        <label className="block" htmlFor="adj-qty"><span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Adjustment (+ or −)</span><input id="adj-qty" data-testid="adjust-qty" type="number" className={`mt-1.5 ${INPUT}`} value={qty} onChange={e => setQty(e.target.value)} placeholder="e.g. +10 or -5" /></label>
        <label className="block" htmlFor="adj-reason"><span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Reason (optional)</span><input id="adj-reason" data-testid="adjust-reason" type="text" className={`mt-1.5 ${INPUT}`} value={reason} onChange={e => setReason(e.target.value)} placeholder="Physical count correction" /></label>
        {qty && <div className="bg-gray-50 rounded-xl border border-gray-200 p-4"><p className="text-gray-500 text-xs uppercase font-semibold tracking-wider">New Stock</p><p className="text-2xl font-extrabold text-teal-600 mt-1">{(item?.stock_qty||0)+(parseFloat(qty)||0)}</p></div>}
        <button data-testid="adjust-save-btn" onClick={submit} disabled={saving} className="w-full h-12 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-bold text-sm hover:shadow-lg hover:shadow-teal-500/25 transition-all disabled:opacity-50 mt-2">
          {saving ? <div className="w-5 h-5 border-2 border-white/50 border-t-white rounded-full animate-spin mx-auto" /> : 'Adjust Stock'}
        </button>
      </div>
    </Drawer>
  );
}
